/**
 * Hinweisleiste „was ist hier zu tun" (Spec 2026-09-16, §3.7). Zeigt die vom Server
 * abgeleitete Erinnerung (`project.reminder`) mit dem Handlungstext aus der Admin-Matrix
 * — dieselben Texte wie in der Tagesuebersicht. Dauerhaft sichtbar, solange der
 * Zustand besteht; kein X, kein Merken (Zustandsanzeige wie das Blocker-Badge).
 *
 * PV-052 (Feedback 1, F5): Befunde stehen als eigener Block UNTER dem Handlungshinweis (bzw.
 * allein) — Überschrift „N Befund(e)“, Rand/Badge in der Farbe der höchsten Stufe, beim Öffnen
 * des Projekts immer aufgeklappt (Zuklappen wird nicht gespeichert). Der Handlungshinweis bleibt
 * unverändert mit seinen Knöpfen; ein Ladefehler erscheint weiter in der Leiste.
 */
import {
  BEFUND_STUFE_RANG,
  type BefundAnzeige,
  type BefundStufe,
  type ProjectReminder,
} from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { Badge } from './badge.js';
import { STUFE_FARBE, STUFE_TON } from './befunde/befund-basis.js';
import { BefundZeile } from './befunde/befund-zeile.js';

export function ReminderBar({
  reminder,
  canEdit,
  onMovePhase,
  onUnblock,
  onEditDueDate,
  onOpenHandover,
  befunde,
  befundeFehler,
}: {
  reminder: ProjectReminder | null | undefined;
  canEdit: boolean;
  onMovePhase: () => void;
  onUnblock: () => void;
  onEditDueDate: () => void;
  onOpenHandover: () => void;
  /** PV-052: nicht erledigte Befunde des Projekts; nur bei Flag `befunde` gesetzt. */
  befunde?: readonly BefundAnzeige[] | undefined;
  /** PV-052 L1: Befund-Abfrage ist fehlgeschlagen — Leiste zeigt einen Fehlertext statt leer zu bleiben. */
  befundeFehler?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en' : 'de';
  const matrixQuery = useQuery({
    queryKey: ['notif-action-matrix', locale],
    queryFn: () => api.getActionMatrix(locale),
    staleTime: 10 * 60 * 1000,
    enabled: !!reminder,
  });
  // Texte noch nicht da → Hinweis-Teil lieber weglassen als leer zeigen.
  const texts = reminder ? matrixQuery.data?.items[reminder.kind] : undefined;
  const hinweis = reminder && texts ? { reminder, texts } : null;
  const anzahlBefunde = befunde?.length ?? 0;
  if (!hinweis && anzahlBefunde === 0 && !befundeFehler) return null;

  let kopf: ReactNode = null;
  let tone: 'warn' | 'info' | 'neutral' = 'neutral';
  if (hinweis) {
    const overdue = hinweis.reminder.event === 'reminder_overdue';
    const isHandover = hinweis.reminder.event === 'handover_incomplete';
    const isBlocker = hinweis.reminder.kind === 'wiedervorlage';
    const action =
      overdue || isHandover ? hinweis.texts.action_overdue : hinweis.texts.action_due_soon;
    const head = isHandover
      ? t('v2.reminder.handover_head', 'Übergabefelder leer')
      : overdue
        ? t('v2.reminder.overdue_head', '{{label}} war am {{date}}', {
            label: hinweis.texts.label,
            date: hinweis.reminder.due_date,
          })
        : t('v2.reminder.due_soon_head', '{{label}} am {{date}}', {
            label: hinweis.texts.label,
            date: hinweis.reminder.due_date,
          });
    tone = overdue || isHandover ? 'warn' : 'info';
    kopf = (
      <>
        <span className="font-medium">{head}</span>
        <span className="text-[color:var(--ink-2)]">— {action}</span>
        {canEdit && (
          <span className="ml-auto flex gap-2">
            {isHandover ? (
              <BarBtn onClick={onOpenHandover}>
                {t('v2.reminder.btn_handover', 'Zu den Übergabefeldern')}
              </BarBtn>
            ) : (
              <>
                {isBlocker ? (
                  <BarBtn onClick={onUnblock}>
                    {t('v2.reminder.btn_unblock', 'Blocker aufheben')}
                  </BarBtn>
                ) : (
                  <BarBtn onClick={onMovePhase}>
                    {t('v2.reminder.btn_move_phase', 'Phase weiterziehen')}
                  </BarBtn>
                )}
                <BarBtn onClick={onEditDueDate}>
                  {t('v2.reminder.btn_due_date', 'Datum anpassen')}
                </BarBtn>
              </>
            )}
          </span>
        )}
      </>
    );
  }

  const farben =
    tone === 'warn'
      ? { background: 'var(--warn-soft)', borderColor: 'var(--warn)' }
      : tone === 'info'
        ? { background: 'var(--primary-soft)', borderColor: 'var(--primary)' }
        : { background: 'var(--surface-2)', borderColor: 'var(--line-soft)' };

  const leiste = hinweis || (anzahlBefunde === 0 && befundeFehler);
  return (
    <div className="space-y-2">
      {leiste && (
        <output
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border px-3 py-2 text-sm"
          style={{ ...farben, color: 'var(--ink)' }}
        >
          {kopf}
          {anzahlBefunde === 0 && befundeFehler && (
            <span
              className={`text-xs ${hinweis && canEdit ? '' : 'ml-auto'}`}
              style={{ color: 'var(--danger)' }}
            >
              ·{' '}
              {t(
                'v2.befund.laden_fehler',
                'Befunde konnten nicht geladen werden. Bitte Seite neu laden.',
              )}
            </span>
          )}
        </output>
      )}
      {befunde && befunde.length > 0 && <BefundBlock befunde={befunde} />}
    </div>
  );
}

/** Eigener Befund-Block (F5): immer offen beim Öffnen, Zuklappen nur für diesen Besuch. */
function BefundBlock({ befunde }: { befunde: readonly BefundAnzeige[] }) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(true);
  const titelId = useId();
  const hoechste = befunde.reduce<BefundStufe>(
    (m, b) => (BEFUND_STUFE_RANG[b.stufe] > BEFUND_STUFE_RANG[m] ? b.stufe : m),
    'info',
  );
  return (
    <section
      aria-labelledby={titelId}
      className="rounded-md border px-3"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        borderLeft: `3px solid ${STUFE_FARBE[hoechste]}`,
      }}
    >
      <div className="flex items-center gap-2 py-2">
        <Badge tone={STUFE_TON[hoechste]}>{t(`v2.befund.stufe_${hoechste}`, hoechste)}</Badge>
        <h3 id={titelId} className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          {t('v2.befund.leiste_anzahl', '{{count}} Befunde', { count: befunde.length })}
        </h3>
        <button
          type="button"
          aria-expanded={offen}
          onClick={() => setOffen((v) => !v)}
          className="text-xs ml-auto"
          style={{ color: 'var(--ink-2)' }}
        >
          {offen
            ? t('v2.befund.block_zuklappen', 'Zuklappen')
            : t('v2.befund.block_aufklappen', 'Aufklappen')}{' '}
          <span aria-hidden="true">{offen ? '▴' : '▾'}</span>
        </button>
      </div>
      {offen && (
        <ul className="border-t" style={{ borderColor: 'var(--line-soft)' }}>
          {befunde.map((b) => (
            <BefundZeile key={b.befund_id} befund={b} mitProjekt={false} />
          ))}
        </ul>
      )}
    </section>
  );
}

function BarBtn({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-2.5 py-1 rounded-md border transition-colors"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        color: 'var(--ink-2)',
      }}
    >
      {children}
    </button>
  );
}
